import type { Display } from "wylo-rc-session";
import type { Session } from "../api/parse.ts";

export type SessionView = "mosaic" | "single";

export type FileProgress = {
  name: string;
  bytes: number;
  direction: "ToDevice" | "ToOperator";
  status: "offering" | "transferring" | "complete" | "aborted";
  reason?: string;
};

export type Screen =
  | { kind: "idle" }
  | { kind: "creating" }
  | { kind: "waiting"; accessMode: Session["accessMode"]; deadlineAt: string | null; requestedAt?: string | null }
  | { kind: "denied" }
  | { kind: "timed-out" }
  | { kind: "connecting" }
  | {
      kind: "live";
      accessMode: Session["accessMode"];
      features: Session["features"];
      displays: Display[];
      selectedDisplayId: string;
      view: SessionView;
      file: FileProgress | null;
    }
  | { kind: "ended"; reason: string }
  | { kind: "failed"; message: string };

export type MediaGrant = {
  kind: "attended" | "unattended";
  session: Session;
};

export type Phase =
  | { kind: "idle" }
  | { kind: "creating" }
  | { kind: "awaiting"; session: Session }
  | { kind: "connecting"; session: Session }
  | {
      kind: "live";
      session: Session;
      displays: Display[];
      selectedDisplayId: string;
      view: SessionView;
      file: FileProgress | null;
    }
  | { kind: "ended"; session: Session | null; reason: string }
  | { kind: "failed"; message: string };

export type Effect = { type: "poll" } | { type: "open-transport" } | { type: "close-transport" };

export type DeskEvent =
  | { type: "create" }
  | { type: "created"; session: Session }
  | { type: "refreshed"; session: Session }
  | { type: "transport-ready"; displays: Display[]; selectedDisplayId: string }
  | { type: "displays"; displays: Display[] }
  | { type: "display-selected"; displayId: string }
  | { type: "set-view"; view: SessionView }
  | { type: "file"; file: FileProgress | null }
  | { type: "ended"; reason: string }
  | { type: "fail"; message: string };

export type Reduction = { phase: Phase; effects: Effect[] };

export function mediaGrant(session: Session): MediaGrant | null {
  if (session.state !== "Active") return null;
  if (session.accessMode === "Attended" && session.consent.state === "Granted") {
    return { kind: "attended", session };
  }
  if (session.accessMode === "Unattended" && session.consent.state === "NotRequired") {
    return { kind: "unattended", session };
  }
  return null;
}

function deadlinePassed(session: Session, now: number): boolean {
  const deadline = session.consent.deadlineAt;
  if (!deadline) return false;
  const at = Date.parse(deadline);
  return Number.isFinite(at) && now >= at;
}

function terminalReason(session: Session): string | null {
  if (session.consent.state === "Denied" || session.disconnectReason === "ConsentDenied") return "ConsentDenied";
  if (session.consent.state === "TimedOut" || session.disconnectReason === "ConsentTimedOut") return "ConsentTimedOut";
  if (session.state === "Ended") return session.disconnectReason ?? "Ended";
  return null;
}

function adopt(session: Session): Reduction {
  const reason = terminalReason(session);
  if (reason) return { phase: { kind: "ended", session, reason }, effects: [{ type: "close-transport" }] };
  if (mediaGrant(session)) return { phase: { kind: "connecting", session }, effects: [{ type: "open-transport" }, { type: "poll" }] };
  if (session.state === "AwaitingConsent") return { phase: { kind: "awaiting", session }, effects: [{ type: "poll" }] };
  return { phase: { kind: "failed", message: "Session cannot start media" }, effects: [{ type: "close-transport" }] };
}

function sameSession(phase: Phase, session: Session): boolean {
  return (phase.kind === "connecting" || phase.kind === "live" || phase.kind === "awaiting") && phase.session.sessionId === session.sessionId;
}

export function reduce(phase: Phase, event: DeskEvent): Reduction {
  switch (event.type) {
    case "create":
      if (phase.kind === "creating" || phase.kind === "connecting" || phase.kind === "live") {
        return { phase, effects: [] };
      }
      return { phase: { kind: "creating" }, effects: [] };
    case "created":
    case "refreshed": {
      if (phase.kind === "live" && event.session.sessionId === phase.session.sessionId && mediaGrant(event.session)) {
        return { phase: { ...phase, session: event.session }, effects: [] };
      }
      if (phase.kind === "connecting" && event.session.sessionId === phase.session.sessionId && mediaGrant(event.session)) {
        return { phase: { ...phase, session: event.session }, effects: [{ type: "poll" }] };
      }
      if (phase.kind === "ended") return { phase, effects: [] };
      if (phase.kind === "awaiting" && !sameSession(phase, event.session) && event.type === "refreshed") {
        return { phase, effects: [] };
      }
      return adopt(event.session);
    }
    case "transport-ready":
      if (phase.kind !== "connecting" || !mediaGrant(phase.session)) {
        return { phase, effects: [{ type: "close-transport" }] };
      }
      return {
        phase: {
          kind: "live",
          session: phase.session,
          displays: event.displays,
          selectedDisplayId: event.selectedDisplayId,
          view: "mosaic",
          file: null,
        },
        effects: [],
      };
    case "displays":
      if (phase.kind !== "live") return { phase, effects: [] };
      return { phase: { ...phase, displays: event.displays }, effects: [] };
    case "display-selected":
      if (phase.kind !== "live") return { phase, effects: [] };
      if (!phase.displays.some((display) => display.displayId === event.displayId)) return { phase, effects: [] };
      return { phase: { ...phase, selectedDisplayId: event.displayId }, effects: [] };
    case "set-view":
      if (phase.kind !== "live") return { phase, effects: [] };
      return { phase: { ...phase, view: event.view }, effects: [] };
    case "file":
      if (phase.kind !== "live") return { phase, effects: [] };
      return { phase: { ...phase, file: event.file }, effects: [] };
    case "ended":
      return {
        phase: { kind: "ended", session: phase.kind === "idle" || phase.kind === "creating" || phase.kind === "failed" ? null : phase.session, reason: event.reason },
        effects: [{ type: "close-transport" }],
      };
    case "fail":
      return { phase: { kind: "failed", message: event.message }, effects: [{ type: "close-transport" }] };
    default: {
      const _never: never = event;
      return _never;
    }
  }
}

export const idlePhase: Phase = { kind: "idle" };

export function screensEqual(left: Screen, right: Screen): boolean {
  if (left === right) return true;
  if (left.kind !== right.kind) return false;
  switch (left.kind) {
    case "idle":
    case "creating":
    case "denied":
    case "timed-out":
    case "connecting":
      return true;
    case "waiting": {
      if (right.kind !== "waiting") return false;
      return left.accessMode === right.accessMode && left.deadlineAt === right.deadlineAt && left.requestedAt === right.requestedAt;
    }
    case "ended": {
      if (right.kind !== "ended") return false;
      return left.reason === right.reason;
    }
    case "failed": {
      if (right.kind !== "failed") return false;
      return left.message === right.message;
    }
    case "live": {
      if (right.kind !== "live") return false;
      return (
        left.accessMode === right.accessMode &&
        left.selectedDisplayId === right.selectedDisplayId &&
        left.view === right.view &&
        left.features === right.features &&
        left.displays === right.displays &&
        left.file === right.file
      );
    }
    default: {
      const _never: never = left;
      return _never;
    }
  }
}

export function screenOf(phase: Phase, now: number): Screen {
  switch (phase.kind) {
    case "idle":
      return { kind: "idle" };
    case "creating":
      return { kind: "creating" };
    case "awaiting":
      if (deadlinePassed(phase.session, now) && phase.session.consent.state === "Pending") {
        return { kind: "timed-out" };
      }
      if (phase.session.consent.state === "Denied") return { kind: "denied" };
      if (phase.session.consent.state === "TimedOut") return { kind: "timed-out" };
      return {
        kind: "waiting",
        accessMode: phase.session.accessMode,
        deadlineAt: phase.session.consent.deadlineAt ?? null,
        requestedAt: phase.session.consent.requestedAt ?? null,
      };
    case "connecting":
      return { kind: "connecting" };
    case "live":
      return {
        kind: "live",
        accessMode: phase.session.accessMode,
        features: phase.session.features,
        displays: phase.displays,
        selectedDisplayId: phase.selectedDisplayId,
        view: phase.view,
        file: phase.file,
      };
    case "ended":
      if (phase.reason === "ConsentDenied") return { kind: "denied" };
      if (phase.reason === "ConsentTimedOut") return { kind: "timed-out" };
      return { kind: "ended", reason: phase.reason };
    case "failed":
      return { kind: "failed", message: phase.message };
    default: {
      const _never: never = phase;
      return _never;
    }
  }
}
